import { takeLatest, call, put, all } from 'redux-saga/effects';
import { isEmpty } from 'lodash';
import {
    OrganizationManagementApi,
    HeaderApi
} from 'api';
import { apiCallStatus } from 'helpers/constants';
import logger from 'helpers/logger';
import { translate } from 'helpers/translateHelper';
import { isAssetInCurrentContext } from 'helpers/assetAndPowertrainFilterHelper';
import { notify } from 'sharedComponents/GeneralNotification';
import { NOTIFICATION_TYPES } from 'sharedComponents/GeneralNotification/constants';
import { refreshSelectedFilters } from '../../../PowertrainConditionMonitoring/ConditionIndicesOverviewV2/actions';
import { actionTypes } from '../constants';
import {
    reloadContextAction,
    getSitesAction,
    getAssetGroupsAction,
    setSelectedFilters
} from '../actions';
import {
    formatSelectedFilters,
} from './helpers';
import {
    formatSiteAssetGroupRequest,
    getDefaultFilters,
    formatAssetsRequest
} from '../helpers';


function* reloadContextSaga({ searchValue, assets, callback, clearQueryParameter, defaultOrganizations, history }) { // GETTING HISTORY FOR NAVIGATION FROM LEGACY ID TO MICRO-SERVICE ID ON ASSET SEARCH FROM GENERAL FILTER
    let organization = null;
    yield put(reloadContextAction(apiCallStatus.LOADING));
    try {
        organization = yield call(OrganizationManagementApi.getOrganizationByAsset, searchValue);

        if (!!organization && isAssetInCurrentContext(searchValue, assets)) {
            callback && callback();
            yield put(reloadContextAction(apiCallStatus.SUCCESS));
            return;
        }

        if (organization) {
            logger.debug(`Organization found for filter value: ${searchValue}`);
            organization.organizationID = organization.organizationId;
            try {
                yield call(OrganizationManagementApi.updateLastSelectedOrganization, organization.organizationId);
                logger.debug('Last selected organization updated via asset search');
            } catch (error) {
                logger.error('Failed to update last selected organization via asset search:', error);
            }
            const assetList = yield call(HeaderApi.getAvailableAssets, formatAssetsRequest([organization]));
            const [sites, assetGroups] = yield all([
                call(HeaderApi.getSites, formatSiteAssetGroupRequest([organization])),
                call(HeaderApi.getAssetGroups, formatSiteAssetGroupRequest([organization]))
            ]);
            yield all([
                put(getSitesAction(apiCallStatus.SUCCESS, sites)),
                put(setSelectedFilters(formatSelectedFilters(organization))),
                put(getAssetGroupsAction(apiCallStatus.SUCCESS, assetGroups)),
            ]);
            // Reset the condition overview state (hierarchy/assets) after the new org is set.
            // This clears any stale loading status from a premature reload triggered by CLEAR_STORE
            // on the search button click, ensuring the hierarchy view reloads with the new org's data.
            yield put(refreshSelectedFilters());
            const searchedAsset = assetList.find((asset) =>
                asset?.ciamSerialNumber?.toString()?.toLowerCase() === searchValue?.toString()?.toLowerCase());
            yield put(reloadContextAction(apiCallStatus.SUCCESS));
            callback && callback();
            // NAVIGATING TO ASSET DETAILS USING MICRO-SERVICE ID
            if (searchedAsset && !isEmpty(searchedAsset) && searchedAsset?.id) {
                history.push({
                    pathname: history?.location?.pathname,
                    search: new URLSearchParams({ assetId: searchedAsset?.id }).toString()
                });
            }
        } else if (defaultOrganizations && defaultOrganizations?.length) {
            logger.debug(`Organization NOT found for filter value: ${searchValue}, will use default organization`);
            notify(translate('ABB.Powertrain.Frontend.noAssetFound'), NOTIFICATION_TYPES.ERROR, true);
            clearQueryParameter();

            const [sites, assetGroups] = yield all([
                call(HeaderApi.getSites, formatSiteAssetGroupRequest(defaultOrganizations)),
                call(HeaderApi.getAssetGroups, formatSiteAssetGroupRequest(defaultOrganizations))
            ]);
            yield all([
                put(getSitesAction(apiCallStatus.SUCCESS, sites)),
                put(getAssetGroupsAction(apiCallStatus.SUCCESS, assetGroups)),
                put(setSelectedFilters({
                    ...getDefaultFilters(),
                    organizations: defaultOrganizations,
                }))
            ]);
            yield put(reloadContextAction(apiCallStatus.SUCCESS));
        } else {
            logger.debug(`Organization not found for filter value: ${searchValue}`);
            notify(translate('ABB.Powertrain.Frontend.noAssetFound'), NOTIFICATION_TYPES.ERROR, true);
            clearQueryParameter();
            yield put(reloadContextAction(apiCallStatus.SUCCESS));
        }
    } catch (err) {
        logger.debug(err);
        yield put(reloadContextAction(apiCallStatus.SUCCESS));
    }
}

function* searchOrgSaga({ orgId, organizations, isAdmin, isCIAMSyncFlag }) {
    yield put(reloadContextAction(apiCallStatus.LOADING));
    try {
        const orgFromUrl = organizations?.find(o => String(o.organizationId) === String(orgId) || String(o.organizationID) === String(orgId));

        if (orgFromUrl) {
            const normalizedOrg = {
                ...orgFromUrl,
                organizationID: orgFromUrl.organizationId || orgFromUrl.organizationID
            };

            logger.debug(`Organization found for orgId: ${orgId}`);

            try {
                yield call(OrganizationManagementApi.updateLastSelectedOrganization, normalizedOrg.organizationID);
                logger.debug('Last selected organization updated via org search');
            } catch (error) {
                logger.error('Failed to update last selected organization via org search:', error);
            }

            const [sites, assetGroups] = yield all([
                call(HeaderApi.getSites, formatSiteAssetGroupRequest([normalizedOrg])),
                call(HeaderApi.getAssetGroups, formatSiteAssetGroupRequest([normalizedOrg]))
            ]);

            yield all([
                put(getSitesAction(apiCallStatus.SUCCESS, sites)),
                put(getAssetGroupsAction(apiCallStatus.SUCCESS, assetGroups)),
                put(setSelectedFilters(formatSelectedFilters(normalizedOrg))),
            ]);

            // Load available assets for this organization
            let refreshFlag = true;
            if (isAdmin && !isCIAMSyncFlag) {
                refreshFlag = false;
            }
            yield call(HeaderApi.getAvailableAssets, { ...formatAssetsRequest([normalizedOrg]), refreshData: refreshFlag });

            yield put(reloadContextAction(apiCallStatus.SUCCESS));
        } else {
            logger.debug(`Organization not found for orgId: ${orgId}`);
            yield put(reloadContextAction(apiCallStatus.SUCCESS));
        }
    } catch (err) {
        logger.error('Error in searchOrgSaga:', err);
        yield put(reloadContextAction(apiCallStatus.SUCCESS));
    }
}

export function* reloadContextWatchers() {
    yield takeLatest(actionTypes.ASSET_SEARCH_REQUEST, reloadContextSaga);
    yield takeLatest(actionTypes.ORG_SEARCH_REQUEST, searchOrgSaga);
}
